import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ArrowDown, ArrowUp, Boxes } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { name: "", unit: "roll", stock_qty: 0, unit_cost: 0, reorder_level: 0, notes: "" };

const SuppliesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const [stockDialog, setStockDialog] = useState(null); // {supply, type: 'restock' | 'consume'}
  const [stockQty, setStockQty] = useState(0);
  const [stockMeta, setStockMeta] = useState({ unit_cost: "", vendor: "", reason: "" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/supplies");
      setItems(data);
    } catch { toast.error("Failed to load supplies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        stock_qty: parseFloat(form.stock_qty) || 0,
        unit_cost: parseFloat(form.unit_cost) || 0,
        reorder_level: parseFloat(form.reorder_level) || 0,
      };
      if (editing) await api.patch(`/supplies/${editing.supply_id}`, payload);
      else await api.post("/supplies", payload);
      toast.success("Saved");
      setOpen(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supply "${s.name}"?`)) return;
    try { await api.delete(`/supplies/${s.supply_id}`); toast.success("Deleted"); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitStock = async () => {
    if (!stockDialog) return;
    try {
      const path = stockDialog.type === "restock"
        ? `/supplies/${stockDialog.supply.supply_id}/restock`
        : `/supplies/${stockDialog.supply.supply_id}/consume`;
      const body = stockDialog.type === "restock"
        ? { qty: parseFloat(stockQty) || 0, unit_cost: stockMeta.unit_cost ? parseFloat(stockMeta.unit_cost) : null, vendor: stockMeta.vendor }
        : { qty: parseFloat(stockQty) || 0, reason: stockMeta.reason };
      await api.post(path, body);
      toast.success(stockDialog.type === "restock" ? "Restocked" : "Consumed");
      setStockDialog(null); setStockQty(0); setStockMeta({ unit_cost: "", vendor: "", reason: "" });
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="supplies-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Inventory</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Supplies</h1>
          <p className="text-sm text-neutral-500 mt-1">Track stock levels, consumption, and restocks.</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="btn-accent inline-flex items-center gap-2" data-testid="supply-create-button">
          <Plus className="h-4 w-4" /> Add supply
        </button>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-neutral-500">
            <Boxes className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
            No supplies tracked yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3">Item</th>
                  <th className="text-left px-4 py-3">Unit</th>
                  <th className="text-right px-4 py-3">Stock</th>
                  <th className="text-right px-4 py-3">Reorder at</th>
                  <th className="text-right px-4 py-3">Unit cost</th>
                  <th className="text-right px-4 py-3">Value</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0] text-sm">
                {items.map((s) => {
                  const low = (s.stock_qty || 0) <= (s.reorder_level || 0);
                  return (
                    <tr key={s.supply_id} data-testid={`supply-row-${s.supply_id}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold">{s.name}</p>
                        {s.notes && <p className="text-xs text-neutral-500">{s.notes}</p>}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{s.unit}</td>
                      <td className={`px-4 py-3 text-right font-bold ${low ? "text-red-600" : "text-[#0B1E3F]"}`}>
                        {s.stock_qty}
                        {low && <span className="ml-1.5 text-[10px] uppercase">LOW</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-neutral-600">{s.reorder_level}</td>
                      <td className="px-4 py-3 text-right text-neutral-600">₱{(s.unit_cost || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-display font-bold">₱{((s.stock_qty || 0) * (s.unit_cost || 0)).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => { setStockDialog({ supply: s, type: "restock" }); setStockQty(0); setStockMeta({ unit_cost: s.unit_cost, vendor: "", reason: "" }); }}
                          data-testid={`supply-restock-${s.supply_id}`}
                          className="p-1.5 hover:bg-green-50 rounded mr-1" title="Restock">
                          <ArrowUp className="h-4 w-4 text-green-700" />
                        </button>
                        <button onClick={() => { setStockDialog({ supply: s, type: "consume" }); setStockQty(0); setStockMeta({ unit_cost: "", vendor: "", reason: "" }); }}
                          data-testid={`supply-consume-${s.supply_id}`}
                          className="p-1.5 hover:bg-yellow-50 rounded mr-1" title="Consume">
                          <ArrowDown className="h-4 w-4 text-yellow-700" />
                        </button>
                        <button onClick={() => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); }}
                          data-testid={`supply-edit-${s.supply_id}`}
                          className="p-1.5 hover:bg-[#F4F6FB] rounded mr-1">
                          <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                        </button>
                        <button onClick={() => remove(s)} data-testid={`supply-delete-${s.supply_id}`} className="p-1.5 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="supply-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit supply" : "Add supply"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Name</label>
              <input className="pav-input mt-1" data-testid="supply-name-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit</label>
                <input className="pav-input mt-1" data-testid="supply-unit-input"
                  value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Stock qty</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1" data-testid="supply-stock-input"
                  value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit cost ₱</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1" data-testid="supply-cost-input"
                  value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Reorder level</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1" data-testid="supply-reorder-input"
                  value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Notes</label>
              <textarea className="pav-input mt-1 min-h-[60px]" data-testid="supply-notes-input"
                value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="supply-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock action dialog */}
      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent className="max-w-md" data-testid="stock-dialog">
          <DialogHeader>
            <DialogTitle>
              {stockDialog?.type === "restock" ? "Restock" : "Consume"}: {stockDialog?.supply?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-neutral-500">Current stock: <span className="font-bold">{stockDialog?.supply?.stock_qty} {stockDialog?.supply?.unit}</span></p>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Quantity</label>
              <input type="number" min="0.01" step="0.01" className="pav-input mt-1"
                data-testid="stock-qty-input"
                value={stockQty} onChange={(e) => setStockQty(e.target.value)} autoFocus />
            </div>
            {stockDialog?.type === "restock" ? (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit cost ₱ (optional)</label>
                  <input type="number" min="0" step="0.01" className="pav-input mt-1"
                    data-testid="stock-cost-input"
                    value={stockMeta.unit_cost} onChange={(e) => setStockMeta({ ...stockMeta, unit_cost: e.target.value })} />
                  <p className="text-xs text-neutral-500 mt-1">If set, an expense entry will be auto-created.</p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Vendor</label>
                  <input className="pav-input mt-1" data-testid="stock-vendor-input"
                    value={stockMeta.vendor} onChange={(e) => setStockMeta({ ...stockMeta, vendor: e.target.value })} />
                </div>
              </>
            ) : (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Reason</label>
                <input className="pav-input mt-1" data-testid="stock-reason-input"
                  placeholder="e.g. Order PAV-260607-ABCDEF"
                  value={stockMeta.reason} onChange={(e) => setStockMeta({ ...stockMeta, reason: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setStockDialog(null)} className="btn-outline text-sm">Cancel</button>
            <button onClick={submitStock} className="btn-primary text-sm" data-testid="stock-submit-button">
              {stockDialog?.type === "restock" ? "Restock" : "Consume"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliesPage;
